import React, { useState, useEffect } from 'react';
import { Box, Drawer, Card, CardContent, CardHeader, Typography, CircularProgress } from '@mui/material';
import api from "../../../../services/api";
import FormAddEditToolbar from "../../../../components/global/AddEditBar";
import { useNotifier } from "muinotify";
import ExpensesForm from "../ExpensesForm";
import useSWR, { mutate } from 'swr';
import ErrorNotification from '../../../../components/ErrorNotification/ErrorNotification';
import * as yup from 'yup';

const validationSchema = yup.object().shape({
  p_id: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
  pci_id: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
  exp_inv_no: yup.string()
    .required('Το πεδίο είναι υποχρεωτικό')
    .matches(/^[\x00-\x7F]*$/, 'Επιτρέπονται μόνο λατινικοί χαρακτήρες και σύμβολα'),
  exp_beneficiary_id: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
  exp_amount: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
  exp_assigned_episodes: yup
    .array()
    .of(yup.number().required('Κάθε επεισόδιο πρέπει να είναι ακέραιος'))
    .required('Απαιτήται επιλογή επεισοδίων')
    .min(1, 'Πρέπει να επιλεχθεί τουλάχιστον ένα επεισόδιο'),
  // exp_date: yup.nullable()
  //     .max(new Date(), "Date cannot be in the future")
});

const validationSchemaIsPrediction = yup.object().shape({
  p_id: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
  pci_id: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
  exp_inv_no: yup.string()
    .matches(/^[\x00-\x7F]*$/, 'Επιτρέπονται μόνο λατινικοί χαρακτήρες και σύμβολα')
    .nullable(true),
  exp_beneficiary_id: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
  exp_assigned_episodes: yup
    .array()
    .of(yup.number().required('Κάθε επεισόδιο πρέπει να είναι ακέραιος'))
    .required('Απαιτήται επιλογή επεισοδίων')
    .min(1, 'Πρέπει να επιλεχθεί τουλάχιστον ένα επεισόδιο'),
  // exp_date: yup.nullable()
  //     .max(new Date(), "Date cannot be in the future")
});

const headerNames = {
  p_id: 'Production ID',
  pci_id: 'PCI ID',
  exp_inv_no: 'Export Invoice Number',
  exp_beneficiary_id: 'Export Beneficiary ID',
  exp_amount: 'Export Amount',
  exp_assigned_episodes: 'Assigned Episodes'
};

const URL = `expense`;

const ProductionEdit = ({ id, onClose }) => {
  const { notifier } = useNotifier();
  const [state, setState] = useState(null);
  const [errorMessage, setErrorMessage] = useState();
  const [errors, setErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);

  const { data: beneficiaries } = useSWR(`/expense-beneficiary`);
  const { data: productions_ed } = useSWR(`/productions`);
  const { data: production_category_item, error: categoryItemError } = useSWR(
    state?.p_id ? `/production-category-item/of-production/${state.p_id}` : null
  );
  const clientKey = `/${URL}/${id}`;

  if (!state) {
    mutate(clientKey);
  }

  const { data: productions } = useSWR(clientKey, {
    revalidateOnFocus: false,
    revalidateOnMount: true,
    onSuccess: (data) => {
      if (data) {
        setState(data);
        setDrawerOpen(true);
        setIsUpdating(false);
      }
    },
    onError: (err) => {
      setErrorMessage(err);
    }
  });

  function updateField(fieldName, value) {
    setState({ ...state, [fieldName]: value })
  }

  const closeDrawer = () => {
    setDrawerOpen(onClose);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (state.exp_pred === true || !state.exp_amount) {
        if (!state.exp_amount) {
          state.exp_amount = 0;
        }
        await validationSchemaIsPrediction.validate(state, { abortEarly: false });

      } else {
        await validationSchema.validate(state, { abortEarly: false });
      }

      if (state.exp_date) {
        state.exp_date = new Date(state.exp_date);
        try {
          state.exp_date.toISOString();
        } catch (error) {
          setErrorMessage(error);
          notifier(<ErrorNotification error={error} />, { variant: "error" });
          return;
        }
      }

      setErrors({});

      const formData = new FormData();
      if (state.file) {
        formData.append("file", state.file, encodeURIComponent(state.file.name));
      }
      formData.append("p_id", parseInt(state.p_id));
      formData.append("pci_id", parseInt(state.pci_id));
      formData.append("exp_inv_no", state.exp_inv_no);
      formData.append("exp_beneficiary_id", parseInt(state.exp_beneficiary_id));
      formData.append("exp_amount", parseFloat(state.exp_amount));
      formData.append("exp_notes", state.exp_notes);
      formData.append("exp_pred", state.exp_pred ? "true" : "false");
      state.exp_assigned_episodes.forEach((episode) =>
        formData.append("exp_assigned_episodes[]", episode)
      );

      formData.append("exp_date", state.exp_date ? state.exp_date.toISOString() : null);

      setIsUpdating(true);
      api.patch(`/${URL}/${parseInt(state.id)}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
        .then((response) => {
          mutate(response);
          notifier("New expense record updated.", { variant: "success" });
          closeDrawer();
        })
        .catch((error) => {
          setIsUpdating(false);
          setErrorMessage(error);
          notifier(<ErrorNotification error={error} />, { variant: "error" });
        })

    } catch (validationErrors) {
      const formattedErrors = {};
      validationErrors.inner.forEach((error) => {
        formattedErrors[error.path] = error.message;
      });
      setErrors(formattedErrors);
    }
  };

  const handleSave = () => {
    const form = document.getElementById(`${URL}-form`);
    if (form) {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={onClose}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        PaperProps={{
          style: {
            width: '100%',
            height: '100vh',
            maxWidth: '800px',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Card
          sx={{
            width: '100%',
            maxWidth: 800,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden', // Prevent overflow from Card itself
          }}
        >
          {/* Header Section */}
          <Box display="flex" justifyContent="center" alignItems="center" width="100%">
            <CardHeader
              title={<Typography variant="h3" gutterBottom={false}>Επεξεργασία Εξόδου</Typography>}
              sx={{
                padding: '8px 16px', // Adjust padding to your design
              }}
            />
          </Box>

          {/* Scrollable Content Section */}
          <CardContent
            sx={{
              display: 'flex',
              height: '100vh',
              flexGrow: 1, // Allow this section to grow and shrink
              overflowY: 'auto',
              margin: 'initial',
            }}
          >
            <form id={`${URL}-form`} onSubmit={handleSubmit}>
              <ExpensesForm
                state={state}
                errors={errors}
                updateField={updateField}
                headerNames={headerNames}
                beneficiaries={beneficiaries}
                productions={productions_ed}
                production_category_item={production_category_item}
              />
            </form>
            {isUpdating && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  zIndex: 1000,
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </CardContent>

          {/* Toolbar (Footer Section) */}
          <Box>
            <FormAddEditToolbar
              handleSave={handleSave}
              handleCancel={onClose}
            />
          </Box>
        </Card>
      </Drawer>

    </>
  );
};

export default ProductionEdit;
